import React, { useContext, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import moment from 'moment';
import { useDispatch } from 'react-redux';

import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';

import { UserSessionContext } from 'components/UserSession';
import removeContact from 'network/removeContact';
import { removeContactById } from 'models/contacts';
import UserAvatar from 'components/UserAvatar';
import CircularLoadingIndicator from 'components/indicators/CircularLoadingIndicator';
import useTwilioEventFunctions from 'components/twilio/hooks/useTwilioEventFunctions';
import { FETCH_ASSETS_URL } from 'appenv';

const ChatIcon = `${FETCH_ASSETS_URL}/assets/chat-filled.svg`;
const TrashIcon = `${FETCH_ASSETS_URL}/assets/trash.svg`;

const StyledIconBtn = styled.div`
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
  }
`;

const NameCell = styled(TableCell)`
  font-size: 14px;
  font-weight: bold;
  display: flex;
  align-items: center;
  border: none;
`;

const StyledName = styled.div`
  margin-left: 11px;
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const DataCell = styled(({ isCapitalize, ...props }) => <TableCell {...props} />)`
  font-size: 12px;
  border: none;
  text-transform: ${({ isCapitalize }) => (isCapitalize ? 'capitalize' : 'none')};
`;

const Divider = styled(TableCell)`
  background-color: #DBDEE2;
  height: 1px;
  padding: 0;
  border: none;
`;

const StyledLoadingIndicator = styled(CircularLoadingIndicator)`
  margin: 0;
`;

const ContactListItem = ({
  contactId,
  name,
  role,
  email,
  description,
  createdAt,
}) => {
  const [isLoading, setLoading] = useState(false);
  const { createPrivateChat } = useTwilioEventFunctions();
  const { userSession } = useContext(UserSessionContext);
  const onPrivateChatBtnClick = useCallback(async () => {
    window.trackingEventToFirebase(null, 'Chat', 'OpenPrivateChatWindow', 'Business_Contact_List_Click');
    setLoading(true);
    await createPrivateChat(contactId);
    setLoading(false);
  }, [contactId, createPrivateChat]);

  const dispatch = useDispatch();
  const formattedDate = moment.utc(createdAt, 'YYYY-MM-DD').format('DD MMM YYYY UTC');
  const handleRemoveContact = async () => {
    const userId = userSession?.rongcloudUserId;
    const success = await removeContact(userId, contactId);
    if (success) {
      dispatch(removeContactById(contactId));
    }
  };

  return (
    <>
      <TableRow>
        <NameCell>
          <UserAvatar name={name || 'empty'} />
          <StyledName>{name || 'empty'}</StyledName>
        </NameCell>
        <DataCell>{email || 'empty'}</DataCell>
        <DataCell isCapitalize>{role || 'attendee'}</DataCell>
        <DataCell isCapitalize>{description || 'empty'}</DataCell>
        <DataCell>{formattedDate}</DataCell>
        <DataCell>
          {
            isLoading ? (
              <StyledLoadingIndicator size={24} />
            ) : (
              <StyledIconBtn onClick={onPrivateChatBtnClick}>
                <img src={ChatIcon} alt="chat" />
              </StyledIconBtn>
            )
          }
        </DataCell>
        <DataCell>
          <StyledIconBtn onClick={handleRemoveContact}>
            <img src={TrashIcon} alt="delete" />
          </StyledIconBtn>
        </DataCell>
      </TableRow>
      <TableRow>
        <Divider colSpan={7} />
      </TableRow>
    </>
  );
};

ContactListItem.propTypes = {
  contactId: PropTypes.string,
  name: PropTypes.string,
  email: PropTypes.string,
  role: PropTypes.string,
  description: PropTypes.string,
  createdAt: PropTypes.string,
};

ContactListItem.defaultProps = {
  contactId: '',
  name: '',
  email: '',
  role: '',
  description: '',
  createdAt: '',
};

export default ContactListItem;
